import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  CheckoutTerms,
  RegistrationTerms,
  redeemGiftCardLanguages,
  purchaseGiftCardLanguages,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import GiftCardBanner from "@/assets/images/brandedCalloutBg.jpg";
import MobileFindGamesBanner from "public/images/magic/mobile-banner.jpg";
import FindGamesBanner from "public/images/magic/magic_ChooseYourGamesBanner.jpeg";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const config: TeamConfig = {
  teamSchema: "flex_sandbox",
  browserTitle: "Flex Sandbox",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  teamName: "Flex Sandbox",

  shouldCheckZipCode: false,

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10215-devils-sec",
    mapId: "main",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: true,
    isLandingPageGiftCardEnabled: true,
    isStandalonePageGiftCardEnabled: true,
    redirectGiftCardUrl: routes.giftCard,
  },

  packs: {
    enabled: true,
    list: [
      {
        id: 1,
        amount: 3,
        title: "3 Game Pack",
        description: "Choose a total of 3 games across four tiers!",
        offerMoreMessage: "Would you like to add 2 more games?",
        buckets: [
          {
            id: 1,
            title: "Pick 3 games",
            description: "Pick 3 Game",
            max: 4,
            min: 3,
            displayAllEvents: true,
          },
        ],
        successMessage:
          "Congrats! You've selected your 3 games for your 3 Game Pack.",
        updatedAccountGroup: 0,
      },
      {
        id: 2,
        amount: 5,
        title: "5 Game Pack",
        description: "Choose a total of 5 games across four tiers!",
        offerMoreMessage: "Would you like to add 2 more games?",
        defaultSelected: true,
        buckets: [
          {
            id: 1,
            title: "Tier 1",
            description: "Pick 2 Games",
            displayAllEvents: true,
            max: 9,
            min: 5,
          },
        ],
        updatedAccountGroup: 0,
        successMessage:
          "Congrats! You've selected your 5 games for your 5 Game Pack.",
        unlockBonus: {
          bonusEventCodes: ["SS6"],
          congratsModal: {
            title: "Congrats!",
            description: "You've unlocked a special event!",
          },
        },
      },
      {
        id: 3,
        amount: 10,
        title: "10 Game Pack",
        description: "Choose a total of 10 games across four tiers!",
        offerMoreMessage: "Would you like to add 2 more games?",
        defaultSelected: true,
        buckets: [
          {
            id: 1,
            title: "Pick 10 Games",
            description: "Pick 10 Games",
            displayAllEvents: true,
            max: Infinity,
            min: 10,
          },
        ],
        updatedAccountGroup: 0,
        successMessage:
          "Congrats! You've selected your 10 games for your 10 Game Pack.",
        unlockBonus: {
          bonusEventCodes: ["SS8"],
          congratsModal: {
            title: "Congrats!",
            description: "You've unlocked a special event!",
          },
        },
      },
    ],
  },

  // disabled routes
  disabledRoutes: [],

  phoneNumber: "",

  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    maxGameSelectionCount: 5,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: false,

    // show additional details fields at checkout form
    isCheckoutAdditionalDetailsEnabled: false,

    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,

    isPerksEnabled: false,
    invPreviewAccountGroup: ["0"],
    allowCheckoutFromPreview: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: true,
    isStandAlonePurchaseCreditEnabled: true,
    showPurchaseCreditLinkInHomeMenu: true,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: false,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },
  isSaveCardAllowed: false,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/baabal/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: "",
    checkoutStatusPageImage: "",
    //TODO: update const to the other teams
    giftCardStandalonePageImage: GiftCardBanner.src,
    logoImage:
      "http://ss-leagues.s3.amazonaws.com/NBA/Updated%20NBA%20Logos/orl.png?width=76&height=76",
    logoNameImage: "",
    faviconImage: "",
    seatMap3dPreviewImage: "",
    mobileFindGamesBannerImage: MobileFindGamesBanner.src,
  },

  colors: {
    primary: "#0077c0",
  },

  howItWorks,
  faqs,

  uiConfigs: {
    bannerImage: {
      title: "Choose Your Games",
      isBackgroundBasedBanner: true,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },
  },

  languages: {
    topNavMenuItems: <></>,
    checkoutTermsAndCondition: <CheckoutTerms />,
    registrationTermsAndCondition: <RegistrationTerms />,
    purchaseSeats: null,
    purchaseGiftCard: purchaseGiftCardLanguages,
    redeemGiftCard: redeemGiftCardLanguages,
    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. They can use the gift card for all events at the Hippodrome Broadway Series in Baltimore, Maryland.",
          },
          sendNowTrue: {
            description:
              "The Recipient will be sent an email with the unique code for them to redeem their gift card. They can use the gift card for all events at the Hippodrome Broadway Series in Baltimore, Maryland.",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive unique code provided to redeem the value of the gift card to the Ticketmaster account above.",
        },
        logout: {
          description:
            "Please input the case sensitive unique code provided to redeem the value of the gift card to the Ticketmaster.",
        },
        success: {
          description: "has been added to to your account!",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
